/**
 * A generic form component for creating and editing lists. It can be used in
 * contexts like a modal or a dedicated page.
 *
 * @module ui/modules/MyListsForm
 */
import './MyListsForm.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import Button from '@ulta/core/components/Button/Button';
import InputField from '@ulta/core/components/InputField/InputField';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { OVERLAY_TYPE, useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import BlockMessage from '@ulta/components/BlockMessage/BlockMessage';
import ConfirmDelete from '@ulta/components/ConfirmDelete/ConfirmDelete';

import interpolateString from '@ulta/utils/interpolateString/interpolateString';
import { getVisualLength } from '@ulta/utils/TextUtils/TextUtils';
import { addYupCustomRegex, isRequiredValidation } from '@ulta/utils/Validations/Validations';

import Text from '../Text/Text';

/**
 * Represents a MyListsForm component
 *
 * @method
 * @param {MyListsFormProps} props - React properties passed from composition
 * @returns {React.ReactElement}
 */
export const MyListsForm = ( props ) => {
  const {
    myListsForm = {},
    createAction,
    saveAction,
    deleteAction,
    invokeMutation,
    deleteDialog,
    contentId,
    confirmDeleteAction,
    componentKey,
    loading
  } = props;
  const { closeOverlay, displayOverlay } = useOverlay();
  const {
    createTitle = {},
    editTitle = {},
    listName = {},
    listId,
    countLabel,
    maxCharacterLimit = 55,
    errorMessage
  } = myListsForm;
  const { breakpoint } = useDeviceInflection() || {};
  const isMobile = breakpoint?.CURRENT_BREAKPOINT === 'SM';
  const [isSubmitting, setIsSubmitting] = useState( false );
  const overlayWasOpen = useRef( displayOverlay );
  const [loader, showLoader, hideLoader] = useLoader( {} );
  const isEditMode = !!listName?.value;
  const formTitle = isEditMode ? editTitle : createTitle;
  const showDelete =  isEditMode && deleteAction && confirmDeleteAction;
  const { text, htmlTag } = formTitle;

  Yup.addMethod( Yup.string, 'isRequiredField', isRequiredValidation );
  addYupCustomRegex( { schemaType: Yup.string, name: 'listName', validations: listName?.validations } );

  const validationSchema = Yup.object( {
    listName: listName?.validations?.[0] && Yup.string().isRequiredField( listName ).listName()
  } );
  const timer = useRef();
  useEffect( () => {
    timer.current = setTimeout( () => {
      composeCloseOverlay(
        {
          isSubmitting,
          overlayWasOpen,
          errorMessage,
          loading
        },
        { closeOverlay,
          hideLoader,
          setIsSubmitting
        } );
    }, 100 );
    return () => clearTimeout( timer.current );
  }, [isSubmitting, loading, errorMessage, closeOverlay, setIsSubmitting, hideLoader] );

  const handleSubmit = useCallback( ( values ) => {
    const action = isEditMode ? saveAction : createAction;
    const listName = values.listName.trim();
    const moduleParams = {
      listName,
      ...( isEditMode && { listId } )
    };
    setIsSubmitting( true );
    composeMutationHandler( {
      action,
      moduleParams,
      contentId
    }, {
      invokeMutation,
      showLoader,
      hideLoader
    } );
  }, [isEditMode, saveAction, createAction, listId, invokeMutation, showLoader, contentId, hideLoader] );

  const handleDelete = useCallback( () => {
    const action = confirmDeleteAction;
    const moduleParams = { listId };
    setIsSubmitting( true );
    composeMutationHandler( {
      action,
      moduleParams,
      contentId
    }, {
      invokeMutation,
      showLoader,
      hideLoader
    } );
  }, [confirmDeleteAction, listId, invokeMutation, showLoader, contentId, hideLoader] );

  return (
    <div className='MyListsForm'>
      { loader }
      <div className='MyListsForm__Content'>
        <div className='MyListsForm__Header'>
          {
            text && (
              <Text textStyle='title-lg' htmlTag={ htmlTag }>
                { text }
              </Text>
            )
          }

        </div>
        <Formik
          initialValues={ { listName: listName?.value || '' } }
          validationSchema={ validationSchema }
          onSubmit={ handleSubmit }
        >
          { ( { values, errors, touched } ) => {
            const submitAction = isEditMode ? saveAction : createAction;
            const isSubmittingOrInvalid = loader || !!errors.listName;
            const isButtonDisabled = isEditMode ?
              ( isSubmittingOrInvalid || values.listName.trim() === listName?.value ) :
              ( isSubmittingOrInvalid || !values.listName.trim() );

            const remainingCharsText =
              !( touched.listName && errors.listName ) && countLabel ?
                getRemainingCharactersText( values.listName, maxCharacterLimit, countLabel, getVisualLength ) :
                null;

            const showErrorMsg = errorMessage?.length > 0 && !isSubmitting;
            return (
              <Form className='MyListsForm__Form' noValidate>
                <div className='MyListsForm__FormBody'>
                  { showErrorMsg &&
                       errorMessage?.map( ( data, index ) => (
                         <div
                           key={ `${componentKey}-${index}` }
                           className='MyListsForm__BlockMessage'
                         >
                           <BlockMessage
                             message={ data.message }
                             messageType={ data.icon }
                             backgroundColor={ data.backgroundColor }
                             borderColor={ data.borderColor }
                             textColor={ data.textColor }
                             dismissAction={ data.dismissAction }
                             action1={ data.action1 }
                             action1Style='link'
                             action2={ data.action2 }
                             ariaLiveType='assertive'
                             role='alert'
                           />
                         </div>
                       ) ) }
                  <div className='MyListsForm__InputField' aria-live='assertive'>
                    <InputField
                      { ...spreadProps( { ...listName } ) }
                      name='listName'
                      maxLength={ maxCharacterLimit }
                      helpText={ remainingCharsText }
                    />
                  </div>
                </div>
                <div className='MyListsForm__Buttons'>
                  { submitAction && (
                    <Button
                      withHover
                      type='submit'
                      label={ submitAction?.label }
                      primary
                      compact
                      width='fullWidth'
                      disabled={ isButtonDisabled }
                    />
                  ) }
                  { showDelete && (
                    <Button
                      withHover
                      type='button'
                      label={ deleteAction?.label }
                      secondary
                      compact
                      width='fullWidth'
                      disabled={ loader }
                      action={ {
                        navigationType: isMobile ? OVERLAY_TYPE.BottomSheet : OVERLAY_TYPE.Modal
                      } }
                      overlayTitle={ {
                        text: deleteDialog?.title,
                        htmlTag: 'h2',
                        textStyle: 'title-lg'
                      } }
                      overlayAriaLabel={ deleteDialog?.title }
                      overlayContent={
                        <ConfirmDelete
                          subTitle={ interpolateString( deleteDialog?.message, [listName?.value] ) }
                          confirmAction={ {
                            label: deleteDialog?.confirmLabel,
                            onClick: handleDelete
                          } }
                          cancelAction={ {
                            label: deleteDialog?.cancelLabel,
                            onClick: closeOverlay
                          } }
                          onClose={ closeOverlay }
                        />
                      }
                    />
                  ) }
                </div>
              </Form>
            );
          } }
        </Formik>
      </div>
    </div>
  );
};

/**
 * Generates the helper text for remaining characters.
 * @param {string} value - The current input value.
 * @param {number} maxLength - The maximum allowed length.
 * @param {string} label - The label for the count (e.g., "characters remaining").
 * @param {(text: string) => number} [lengthCalculator] - Optional function to calculate string length. Defaults to `string.length`.
 * @returns {string} The formatted helper text.
 */
export const getRemainingCharactersText = ( value, maxLength, label, lengthCalculator = ( text ) => ( text || '' ).length ) => {
  const remaining = maxLength - lengthCalculator( value || '' );
  return interpolateString( label, [remaining] );
};

/**
 * Composes the logic for handling GraphQL mutations.
 * @param {object} data - Data needed for the mutation.
 * @param {function} data.invokeMutation - The function to call the mutation.
 * @param {function} data.closeFormOverlay - Callback to close the overlay and hide the loader.
 * @param {object} data.action - The action object containing graphql, customHeaders, and params.
 * @param {object} data.moduleParams - The variables for the mutation.
 */
export const composeMutationHandler = ( data, methods ) => {
  const { action, moduleParams, contentId } = handleEmptyObjects( data );
  const { showLoader, hideLoader, invokeMutation } = handleEmptyObjects( methods );
  const { customHeaders, graphql, params } = action;

  if( !invokeMutation || !graphql ){
    return;
  }

  showLoader();

  invokeMutation( {
    graphql,
    variables: { moduleParams, contentId },
    ...( customHeaders && { customHeaders } ),
    ...( params && { params } )
  } );
};

/**
* composeCloseOverlay - is used to close overlay/flyout section
* @param {object} data - Argument
* @param {boolean} data.loading - Is component loading
* @param {object} methods - object contains methods which are passed as args to the function
* @param {function} methods.closeOverlay - closeOverlay
* @param {function} methods.hideLoader - hideLoader
*/

const composeCloseOverlay = ( data, method ) => {
  const { loading, isSubmitting, overlayWasOpen, errorMessage } = data;
  const { closeOverlay, hideLoader, setIsSubmitting } = method;
  if( overlayWasOpen.current && isSubmitting && !loading ){
    if( !errorMessage?.length ){
      closeOverlay();
    }
    setIsSubmitting( false );
    hideLoader();
  }
};

/**
 * Property type definitions
 * @typedef MyListsFormProps
 * @type {object}
 * @property {object} myListsForm - Object containing all the configuration for the form.
 * @property {object} [myListsForm.createTitle] - Title for the create new list form, containing `text` and `htmlTag`.
 * @property {object} [myListsForm.editTitle] - Title for the edit existing list form, containing `text` and `htmlTag`.
 * @property {object} [myListsForm.listName] - The configuration for the list name input field, including label, value, and validations.
 * @property {string} [myListsForm.listId] - The ID of the list for edit mode.
 * @property {string} myListsForm.countLabel - Label for the remaining characters count.
 * @property {number} myListsForm.maxCharacterLimit - The maximum number of characters for the list name.
 * @property {array} [myListsForm.errorMessage] - An error message to display (e.g., from a server response).
 * @property {object} createAction - Object containing the label and the async action function to call on form submission in create mode. The action function should return a promise.
 * @property {object} saveAction - Object containing the label and the async action function to call on form submission in edit mode. The action function should return a promise.
 * @property {object} deleteAction - Object containing the label and the async action function to call when the delete button is clicked. The action function should return a promise.
 * @property {object} [deleteDialog] - Configuration for the delete confirmation dialog.
 * @property {string} [componentKey] - A unique key for the component instance, used for generating stable keys for child elements.
 */
export const propTypes = {
  myListsForm: PropTypes.shape( {
    createTitle: PropTypes.shape( {
      text: PropTypes.string,
      htmlTag: PropTypes.string
    } ),
    editTitle: PropTypes.shape( {
      text: PropTypes.string,
      htmlTag: PropTypes.string
    } ),
    listName: PropTypes.shape( {
      label: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.string,
      validations: PropTypes.array
    } ),
    listId: PropTypes.string,
    countLabel: PropTypes.string.isRequired,
    maxCharacterLimit: PropTypes.number,
    errorMessage: PropTypes.array
  } ).isRequired,
  createAction: PropTypes.object.isRequired,
  saveAction: PropTypes.object.isRequired,
  deleteAction: PropTypes.object.isRequired,
  deleteDialog: PropTypes.object,
  /**
   * A unique key for the component instance, used for generating stable keys for child elements.
   */
  componentKey: PropTypes.string
};

MyListsForm.defaultProps = {
  myListsForm: {
    listName: {
      value: ''
    },
    maxCharacterLimit: 55,
    errorMessage: []
  },
  componentKey: 'my-lists-form'
};

MyListsForm.propTypes = propTypes;

export default MyListsForm;
